/** @jsxImportSource react */
import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { X } from 'lucide-react'

interface ModalProps {
  children: React.ReactNode
  title: string
  onClose: () => void
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
}

export const Modal: React.FC<ModalProps> = ({
  children,
  title,
  onClose,
  size = 'md',
}) => {
  const modalRef = useRef<HTMLDivElement>(null)
  
  useEffect(() => {
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden'
    // Handle escape key
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleEsc)
    }
  }, [onClose])

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/75 backdrop-blur-md animate-fade-in"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-xl shadow-xl w-full relative animate-modal-in ${sizeClasses[size]} flex flex-col max-h-[90vh]`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative elements */}
        <div className="absolute -top-32 -left-32 w-64 h-64 bg-primary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
        <div className="absolute -bottom-32 -right-32 w-64 h-64 bg-secondary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
        
        {/* Modal Header */}
        <div className="relative z-10 flex justify-between items-center border-b border-slate-100 p-5 flex-shrink-0">
          <h3 className="text-xl font-bold text-slate-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-full p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Modal Content */}
        <div className="relative z-10 overflow-y-auto p-6">
          {children}
        </div>
      </div>
    </div>
  );

  // This will be null on the first render, so we check for it.
  const modalRoot = typeof document !== 'undefined' ? document.getElementById('modal-root') : null;

  if (!modalRoot) {
    // If we're server-side rendering or the element doesn't exist,
    // we can return null or a fallback. For this client-side app,
    // we'll log an error and render inline as a fallback.
    if (typeof document !== 'undefined') {
      console.error("The element with id 'modal-root' was not found in the DOM.");
    }
    return modalContent;
  }
  
  return ReactDOM.createPortal(modalContent, modalRoot);
}